import React, { useEffect, useState } from 'react';
import { Button, Spinner, Modal, Form } from 'react-bootstrap';
import axios from 'axios';
import '../styles/careers.css';
import CareerInterestForm from './CareerInterestForm';
import { API_BASE } from '../lib/apiBase';
import PageHeader from './PageHeader';

/**
 * Careers landing page outlines perks, current vacancies (if any), and embeds
 * the interest registration form so prospective staff can lodge their details.
 */
const formatDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString('en-AU', { year: 'numeric', month: 'long', day: 'numeric' });
};

const Careers = () => {
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobsError, setJobsError] = useState('');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applicationForm, setApplicationForm] = useState({
    fullName: '',
    email: '',
    phone: '',
  });
  const [resumeFile, setResumeFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [applicationError, setApplicationError] = useState('');

  useEffect(() => {
    let ignore = false;

    const loadJobs = async () => {
      try {
        setJobsError('');
        const res = await fetch(`${API_BASE}/jobs`);
        if (!res.ok) throw new Error('Failed to fetch jobs');
        const data = await res.json();
        if (!ignore) {
          setJobs(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error('Failed to load job listings', err);
        if (!ignore) setJobsError('Unable to load job openings right now. Please try again later.');
      } finally {
        if (!ignore) setJobsLoading(false);
      }
    };

    loadJobs();

    return () => {
      ignore = true;
    };
  }, []);

  const hasJobs = jobs && jobs.length > 0;

  const resetApplicationState = () => {
    setApplicationForm({ fullName: '', email: '', phone: '' });
    setResumeFile(null);
    setCoverFile(null);
  };

  const openApplicationModal = (job) => {
    setSelectedJob(job);
    resetApplicationState();
    setApplicationMessage('');
    setApplicationError('');
    setShowApplyModal(true);
  };

  const closeApplicationModal = () => {
    setShowApplyModal(false);
    setSelectedJob(null);
    setApplicationMessage('');
    setApplicationError('');
  };

  const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleApplicationSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;

    setApplicationError('');
    setApplicationMessage('');

    if (!applicationForm.fullName.trim()) {
      setApplicationError('Full name is required.');
      return;
    }

    if (!resumeFile) {
      setApplicationError('Please upload your resume.');
      return;
    }

    try {
      setSubmittingApplication(true);
      const resumeBase64 = await readFileAsBase64(resumeFile);
      const coverBase64 = coverFile ? await readFileAsBase64(coverFile) : undefined;

      await axios.post(
        `${API_BASE}/jobs/${selectedJob.id || selectedJob._id}/applications`,
        {
          fullName: applicationForm.fullName.trim(),
          email: applicationForm.email.trim() || undefined,
          phone: applicationForm.phone.trim() || undefined,
          resume: {
            filename: resumeFile.name,
            mimetype: resumeFile.type,
            base64: resumeBase64,
          },
          coverLetter: coverBase64
            ? {
                filename: coverFile.name,
                mimetype: coverFile.type,
                base64: coverBase64,
              }
            : undefined,
        }
      );

      setApplicationError('');
      setApplicationMessage('Thanks! Your application has been submitted.');
      resetApplicationState();
    } catch (err) {
      console.error('Failed to submit application', err);
      const message = err?.response?.data?.error || 'Unable to submit application. Please try again.';
      setApplicationError(message);
    } finally {
      setSubmittingApplication(false);
    }
  };

  return (
    <div className="careers-page">
      <PageHeader
        title="Careers"
        image="/media/gallery-3.jpg"
        height="200px"
      />
      <section className="careersIntro">
        <div className="careersIntro__inner">
          <p className="careersIntro__eyebrow">THE AVENUE SCHOOL</p>
          <h1 className="careersIntro__title">Careers.</h1>
          <p className="careersIntro__lead">
            Join Our Team at Avenue School
            <br />
            We’re looking for passionate, dedicated professionals to help shape the future of our students.
          </p>
          <h2 className="careersIntro__subheading">Why Work at The Avenue School</h2>
          <p className="careersIntro__benefits">
            Supportive, inclusive team environment
            <br />
            Modern teaching facilities
            <br />
            Opportunities for professional growth
            <br />
            Engaging school community
          </p>
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="h4 fw-bold text-brand mb-4">Current Job Openings</h2>
          {jobsLoading && (
            <div className="d-flex align-items-center justify-content-center py-4 text-muted">
              <Spinner animation="border" role="status" className="me-2" />
              Loading roles…
            </div>
          )}

          {!jobsLoading && jobsError && (
            <div className="alert alert-danger" role="alert">
              {jobsError}
            </div>
          )}

          {!jobsLoading && !jobsError && !hasJobs && (
            <div className="alert alert-secondary text-center mb-0" role="alert">
              There are currently no job openings. Please check back later.
            </div>
          )}

          {!jobsLoading && !jobsError && hasJobs && (
            <div className="d-flex flex-column gap-4">
              {jobs.map((job) => {
                const qualifications = Array.isArray(job.qualifications) ? job.qualifications : [];
                const closing = formatDate(job.closingDate);
                const salaryFrom = typeof job.salaryFrom === 'number' ? job.salaryFrom : null;
                const salaryTo = typeof job.salaryTo === 'number' ? job.salaryTo : null;
                const period = job.salaryPeriod || 'year';
                const currency = job.salaryCurrency || 'AUD';
                const salaryLabel = salaryFrom || salaryTo
                  ? `${currency} ${salaryFrom ?? '—'}${salaryTo ? ` - ${salaryTo}` : ''} / ${period}`
                  : null;

                const details = [job.type, job.fte].filter(Boolean).join(' • ');

                return (
                  <article key={job.id || job._id} className="card shadow-sm border-0">
                    <div className="card-body p-4">
                      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-2">
                        <div>
                          <h3 className="h5 fw-bold text-brand mb-1">{job.title}</h3>
                          <div className="text-muted">
                            {details || 'Role details pending'}
                          </div>
                        </div>
                        {closing && (
                          <span className="badge bg-warning-subtle text-warning fw-semibold">
                            Applications close {closing}
                          </span>
                        )}
                      </div>

                      <p className="text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                        {job.description}
                      </p>

                      {salaryLabel && (
                        <div className="mb-3">
                          <strong>Salary:</strong> {salaryLabel}
                        </div>
                      )}

                      {qualifications.length > 0 && (
                        <div className="mb-3">
                          <h4 className="h6 fw-semibold">Qualifications & Experience</h4>
                          <ul className="mb-0">
                            {qualifications.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                        <div className="text-muted small">
                          Submit your application through the Avenue School careers portal.
                        </div>
                        <div className="ms-md-auto">
                          <Button variant="primary" onClick={() => openApplicationModal(job)}>
                            Apply Now
                          </Button>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="py-5 bg-light">
        <div className="container">
          <h2 className="h4 fw-bold text-brand mb-3">Register Interest to Work</h2>
          <p className="text-muted mb-4">Not seeing a role yet? Share your details and the positions you’re interested in, and we’ll keep your CV on file.</p>
          <CareerInterestForm />
        </div>
      </section>

      <Modal show={showApplyModal} onHide={closeApplicationModal} size="lg" centered>
        <Form onSubmit={handleApplicationSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {selectedJob ? `Apply for ${selectedJob.title}` : 'Apply'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {applicationMessage && <div className="alert alert-success">{applicationMessage}</div>}
            {applicationError && <div className="alert alert-danger">{applicationError}</div>}

            <Form.Group className="mb-3">
              <Form.Label>Full Name *</Form.Label>
              <Form.Control
                type="text"
                value={applicationForm.fullName}
                onChange={(e) => setApplicationForm((prev) => ({ ...prev, fullName: e.target.value }))}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={applicationForm.email}
                onChange={(e) => setApplicationForm((prev) => ({ ...prev, email: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Phone</Form.Label>
              <Form.Control
                type="text"
                value={applicationForm.phone}
                onChange={(e) => setApplicationForm((prev) => ({ ...prev, phone: e.target.value }))}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cover Letter (PDF, DOC, DOCX)</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              />
              <Form.Text className="text-muted">Optional but recommended.</Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Resume (PDF, DOC, DOCX) *</Form.Label>
              <Form.Control
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setResumeFile(e.target.files?.[0] || null)}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeApplicationModal} disabled={submittingApplication}>
              Close
            </Button>
            <Button type="submit" variant="primary" disabled={submittingApplication}>
              {submittingApplication ? 'Submitting…' : 'Submit Application'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Careers;
